import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";

import { useEscapeKey } from "../lib/useEscapeKey";

type Option<T extends string | number> = {
  value: T;
  label: string;
  description?: string;
};

type Props<T extends string | number> = {
  label: string;
  options: readonly Option<T>[];
  value: T;
  defaultValue: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  formatActive?: (option: Option<T>) => string;
  layout?: "compact" | "field";
  showValue?: boolean;
  disabled?: boolean;
  menuLayout?: "default" | "detailed";
};

export default function FilterDropdown<T extends string | number>({
  label,
  options,
  value,
  defaultValue,
  onChange,
  ariaLabel,
  formatActive,
  layout = "compact",
  showValue = false,
  disabled = false,
  menuLayout = "default",
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();
  const [menuPlacement, setMenuPlacement] = useState({ above: false, height: 384 });
  const detailed = menuLayout === "detailed";

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const isActive = value !== defaultValue;
  const activeOption = options.find((option) => option.value === value);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const activeLabel =
    (showValue || isActive) && activeOption
      ? (formatActive?.(activeOption) ?? activeOption.label)
      : null;
  const fieldValue = activeOption?.label ?? label;
  const isField = layout === "field";

  const triggerToneClass = isActive
    ? "border-slate-200 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100 hover:bg-emerald-100"
    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";
  const triggerLayoutClass = isField
    ? "flex w-full min-w-[14rem] justify-between"
    : "inline-flex";
  const triggerClass = `${triggerLayoutClass} h-9 items-center gap-1.5 rounded-md border px-3 text-sm font-medium shadow-sm ${triggerToneClass}`;

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      const item = itemRefs.current[focusedIndex];
      item?.focus({ preventScroll: detailed });
      if (detailed && item?.parentElement) {
        const menu = item.parentElement;
        if (item.offsetTop < menu.scrollTop) menu.scrollTop = item.offsetTop;
        else if (item.offsetTop + item.offsetHeight > menu.scrollTop + menu.clientHeight) {
          menu.scrollTop = item.offsetTop + item.offsetHeight - menu.clientHeight;
        }
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusedIndex, open, detailed]);

  function openAt(index: number) {
    if (options.length === 0) return;
    if (detailed && triggerRef.current) {
      const bounds = triggerRef.current.getBoundingClientRect();
      const below = window.innerHeight - bounds.bottom - 16;
      const above = bounds.top - 16;
      const opensAbove = below < Math.min(320, above);
      setMenuPlacement({ above: opensAbove, height: Math.min(384, Math.max(80, opensAbove ? above : below)) });
    }
    setFocusedIndex(Math.min(Math.max(index, 0), options.length - 1));
    setOpen(true);
  }

  function closeAndFocusTrigger() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  useEscapeKey(closeAndFocusTrigger, open);

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openAt(selectedIndex);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openAt(options.length - 1);
    }
  }

  function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (options.length === 0) return;

    let nextIndex: number | null = null;
    if (event.key === "ArrowDown") {
      nextIndex = (focusedIndex + 1) % options.length;
    } else if (event.key === "ArrowUp") {
      nextIndex = (focusedIndex - 1 + options.length) % options.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = options.length - 1;
    }
    if (nextIndex !== null) {
      event.preventDefault();
      setFocusedIndex(nextIndex);
    }
  }

  return (
    <div
      ref={ref}
      className={isField ? "relative w-full" : "relative"}
      aria-label={ariaLabel}
    >
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={isField ? `${label}: ${fieldValue}` : undefined}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            openAt(selectedIndex);
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        className={`${triggerClass} disabled:cursor-default disabled:bg-slate-50 disabled:text-slate-400 disabled:shadow-none`}
      >
        {isField ? (
          <span className="truncate">{fieldValue}</span>
        ) : (
          <>
            <span>{label}</span>
            {activeLabel ? (
              <span className="font-semibold text-emerald-700">
                {activeLabel}
              </span>
            ) : null}
          </>
        )}
        <span aria-hidden="true" className="text-xs text-slate-400">
          ▾
        </span>
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label={ariaLabel}
          onKeyDown={handleMenuKeyDown}
          style={detailed ? { maxHeight: menuPlacement.height, ...(menuPlacement.above ? { bottom: "calc(100% + 0.25rem)" } : {}) } : undefined}
          className={[
            "absolute left-0 z-30 mt-1 rounded-md border border-slate-200 bg-white p-1 shadow-lg",
            detailed ? "w-[min(28rem,calc(100vw-3rem))] overflow-y-auto overscroll-contain" : isField
              ? "w-full min-w-[14rem]"
              : "min-w-[11rem]",
          ].join(" ")}
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <button
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                key={String(option.value)}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                tabIndex={focusedIndex === index ? 0 : -1}
                onMouseEnter={() => setFocusedIndex(index)}
                onClick={() => {
                  // Finish menu focus restoration before onChange opens a dialog.
                  closeAndFocusTrigger();
                  onChange(option.value);
                }}
                className={
                  selected
                    ? "grid w-full grid-cols-[minmax(0,1fr)_1.25rem] items-center gap-4 rounded bg-emerald-50 px-2.5 py-1.5 text-left text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                    : "grid w-full grid-cols-[minmax(0,1fr)_1.25rem] items-center gap-4 rounded px-2.5 py-1.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                }
              >
                <span className="min-w-0">
                  <span className={detailed ? "block break-words" : undefined}>{option.label}</span>
                  {option.description && <span className="mt-1 block text-xs font-normal text-slate-600">{option.description}</span>}
                </span>
                {selected ? (
                  <span
                    aria-hidden="true"
                    className="justify-self-end text-emerald-600"
                  >
                    ✓
                  </span>
                ) : (
                  <span aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
